import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import SnackbarUtils from "utils/SnackbarUtils";
import { wmsItemGroupService } from "services";
import type { WmsItemGroup } from "types";

type PersistMeta = {
  pinned?: boolean;
  onRevert?: () => void;
  onSuccess?: () => void;
  refetch?: boolean;
};

const WmsItemGroups: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<WmsItemGroup | null>(null);
  const originalRowDataRef = useRef<Map<string | number, WmsItemGroup>>(
    new Map(),
  );
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [rows, setRows] = useState<WmsItemGroup[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [confirmRow, setConfirmRow] = useState<WmsItemGroup | null>(null);
  const [showInlineAddRow, setShowInlineAddRow] = useState<
    "add" | "edit" | undefined
  >(undefined);
  const [queryParams, setQueryParams] = useState<{
    page: number;
    pageSize: number;
  }>({
    page: 1,
    pageSize: 20,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await wmsItemGroupService.list();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      SnackbarUtils.error("Tải danh sách nhóm hàng hóa thất bại");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo<ColDef<WmsItemGroup>[]>(() => {
    return [
      {
        headerName: "Mã nhóm",
        field: "code",
        minWidth: 140,
      },
      {
        headerName: "Tên nhóm",
        field: "name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Mô tả",
        field: "description",
        minWidth: 220,
        flex: 1,
      },
    ];
  }, []);

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      code: "",
      name: "",
      description: "",
    };
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    async (row: Partial<WmsItemGroup>, meta?: PersistMeta) => {
      const payload: WmsItemGroup = {
        code: String(row.code ?? "").trim(),
        name: row.name ? String(row.name).trim() : undefined,
        description: row.description
          ? String(row.description).trim()
          : undefined,
      };

      if (!payload.code || !payload.name) {
        SnackbarUtils.error("Mã nhóm và Tên nhóm là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      setLoading(true);
      try {
        const shouldCreate = !row.id || meta?.pinned;

        // Store original data before update
        if (!shouldCreate && row.id) {
          const currentRow = rows.find((r) => r.id === row.id);
          if (currentRow) {
            originalRowDataRef.current.set(row.id, { ...currentRow });
          }
        }

        if (shouldCreate) {
          const res = await wmsItemGroupService.create(payload);
          if (res?.data) setRows((prev) => [res.data as WmsItemGroup, ...prev]);
          SnackbarUtils.success(
            res?.message ?? "Thêm nhóm hàng hóa thành công",
          );
        } else {
          const res = await wmsItemGroupService.update(row.id, payload);
          if (res?.data) {
            setRows((prev) =>
              prev.map((item) =>
                item.id === row.id ? (res.data as WmsItemGroup) : item,
              ),
            );
          } else {
            // Revert UI state to original data
            if (row.id && originalRowDataRef.current.has(row.id)) {
              const original = originalRowDataRef.current.get(row.id)!;
              setRows((prev) =>
                prev.map((item) => (item.id === row.id ? original : item)),
              );
              originalRowDataRef.current.delete(row.id);
            } else {
              setRows((prev) =>
                prev.map((item) =>
                  item.id === row.id ? { ...item, ...payload } : item,
                ),
              );
            }
          }
          SnackbarUtils.success(
            res?.message ?? "Cập nhật nhóm hàng hóa thành công",
          );
        }
        if (meta?.refetch) fetchData();
        meta?.onSuccess?.();
      } catch (error) {
        SnackbarUtils.error("Lưu nhóm hàng hóa thất bại");
        meta?.onRevert?.();
      } finally {
        setLoading(false);
      }
    },
    [fetchData],
  );

  const handleSaveRow = useCallback(
    (row: WmsItemGroup, meta?: { pinned?: boolean }) => {
      // CRITICAL: Stop editing to commit cell values
      if (gridApiRef.current) {
        gridApiRef.current.stopEditing();
      }

      // Small delay to ensure commit completes
      setTimeout(() => {
        persistRow(row, {
          pinned: meta?.pinned,
          refetch: meta?.pinned || !row.id,
          onSuccess: () => {
            resetInlineAddRow();
            gridApiRef.current?.stopEditing();
            setShowInlineAddRow("add");
          },
        });
      }, 50);
    },
    [persistRow, resetInlineAddRow],
  );

  const handleEditRow = useCallback((params) => {
    setTimeout(() => {
      gridApiRef.current?.startEditingCell({
        rowIndex: params.node.rowIndex,
        colKey: "code",
      });
    }, 0);
    setShowInlineAddRow(undefined);
  }, []);

  const handleDeleteRow = useCallback((row: WmsItemGroup) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmRow?.id) return;
    setLoading(true);
    try {
      await wmsItemGroupService.destroy(confirmRow.id);
      setRows((prev) => prev.filter((item) => item.id !== confirmRow.id));
      setConfirmRow(null);
    } catch (error) {
      SnackbarUtils.error("Xóa nhóm hàng hóa thất bại");
    } finally {
      setLoading(false);
    }
  }, [confirmRow]);

  const handleQueryChange = useCallback((query: QueryState) => {
    setQueryParams((prev) => {
      const next = {
        ...prev,
        page: query.pagination?.page ?? prev.page,
        pageSize: query.pagination?.pageSize ?? prev.pageSize,
      };
      const unchanged =
        next.page === prev.page && next.pageSize === prev.pageSize;
      return unchanged ? prev : next;
    });
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () =>
      showInlineAddRow && addRowRef.current
        ? { ...addRowRef.current }
        : undefined,
    [addRowVersion, showInlineAddRow],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          Nhóm hàng hóa (WMS)
        </h1>
        <p className="text-sm text-gray-600">
          Danh sách nhóm hàng hóa, cho phép thêm, sửa, xóa trực tiếp trên bảng.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách nhóm hàng
            </h2>
            <p className="text-xs text-gray-500">
              Chỉnh sửa nhanh, lưu trực tiếp.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const next = showInlineAddRow ? undefined : "add";
              if (next) {
                resetInlineAddRow();
              } else {
                addRowRef.current = null;
                setAddRowVersion((v) => v + 1);
              }
              setShowInlineAddRow(next);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {showInlineAddRow ? "Ẩn dòng thêm" : "Thêm nhanh"}
          </button>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={Math.ceil(rows.length / queryParams.pageSize)}
          onSave={handleSaveRow}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onQueryChange={handleQueryChange}
          getRowId={(params) =>
            String(params.data.id ?? `new-${params.rowIndex}`)
          }
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
          onAfterActions={resetInlineAddRow}
        />

        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang tải hoặc lưu dữ liệu nhóm hàng hóa."
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa nhóm hàng?"
        description={`Bạn chắc chắn muốn xóa nhóm hàng "${confirmRow?.code ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={isLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default WmsItemGroups;
